<script setup>
import { computed } from 'vue';
import KanbanCardV2 from './KanbanCardV2.vue';

const props = defineProps({
  stageName: {
    type: String,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: Number,
    required: false,
    default: null,
  },
  isDragOver: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'cardClick',
  'cardRemove',
  'openConversation',
  'openCrm',
  'dragStart',
  'dragOver',
  'dragLeave',
  'drop',
  'toggleChecklist',
  'assignAgent',
  'loadMore',
]);

// Helper method to get mapping for a conversation
const getConversationMapping = conversation => {
  if (conversation.funnel_mappings && conversation.funnel_mappings.length > 0) {
    return conversation.funnel_mappings[0];
  }
  return {};
};

// Computed properties
const conversationCount = computed(() => props.conversations.length);

// Calcular o valor total de todos os produtos/serviços nesta coluna
const totalValue = computed(() => {
  let total = 0;

  props.conversations.forEach(conversation => {
    const products = conversation.conversation_products || [];
    const filteredProducts = props.funnelId
      ? products.filter(
          product => !product.funnel_id || product.funnel_id === props.funnelId
        )
      : products;

    filteredProducts.forEach(product => {
      total += parseFloat(product.amount) || 0;
    });
  });

  return total;
});

// Formatar valor em moeda brasileira
const formattedTotalValue = computed(() => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalValue.value);
});

const stageColor = computed(() => props.stageConfig?.color || '#6366f1');

// Event handlers
const handleCardClick = conversation => {
  // Passa a conversation completa para abrir o painel lateral
  emit('cardClick', conversation);
};

const handleOpenConversation = conversation => {
  emit('openConversation', conversation);
};

const handleOpenCrm = (conversation, contact) => {
  emit('openCrm', conversation, contact);
};

const handleCardRemove = conversation => {
  emit('cardRemove', conversation);
};

const handleDragStart = (conversationId, stageName, event) => {
  emit('dragStart', conversationId, stageName, event);
};

const handleDragOver = event => {
  event.preventDefault();
  emit('dragOver', event, props.stageName);
};

const handleDragLeave = event => {
  emit('dragLeave', event, props.stageName);
};

const handleDrop = event => {
  event.preventDefault();
  emit('drop', event, props.stageName);
};

const handleLoadMore = () => {
  emit('loadMore', props.stageName);
};

// Computed para mostrar texto do botão
const loadMoreText = computed(() => {
  const remaining = props.totalCount - props.conversations.length;
  if (remaining > 0) {
    return `Carregar mais (${remaining})`;
  }
  return 'Carregar mais';
});
</script>

<template>
  <div
    class="kanban-column flex flex-col rounded-xl bg-n-solid-2 min-h-full w-72 border border-n-weak/50"
    :class="[{ 'ring-2 ring-woot-400/40 border-transparent': isDragOver }]"
    :data-column-name="stageName"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- Barra colorida no topo -->
    <div
      class="h-1 w-full rounded-t-xl"
      :style="{ backgroundColor: stageColor }"
    />

    <!-- Header da Coluna -->
    <div class="px-3 py-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div
            class="w-2 h-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: stageColor }"
          />
          <h3
            class="text-xs font-semibold text-n-slate-12 uppercase tracking-wide"
          >
            {{ stageConfig.label || stageName }}
          </h3>
        </div>
        <span
          class="text-xs font-medium text-n-slate-10 bg-n-alpha-2 px-2 py-0.5 rounded-full"
        >
          {{ conversationCount }}
        </span>
      </div>

      <!-- Valor total (opcional) -->
      <div v-if="totalValue > 0" class="mt-1.5 ml-4">
        <span class="text-xs font-medium text-n-teal-11">
          {{ formattedTotalValue }}
        </span>
      </div>
    </div>

    <!-- Lista de conversas -->
    <div
      class="column-content flex-1 px-2 pb-2 space-y-2 overflow-y-auto"
      @dragover.prevent
      @drop="handleDrop"
    >
      <KanbanCardV2
        v-for="conversation in conversations"
        :key="conversation.id"
        :conversation="conversation"
        :stage-config="stageConfig"
        :mapping="getConversationMapping(conversation)"
        :funnel-id="funnelId"
        :stage-name="stageName"
        @click="handleCardClick(conversation)"
        @open-conversation="handleOpenConversation"
        @open-crm="handleOpenCrm"
        @remove-from-funnel="handleCardRemove"
        @drag-start="handleDragStart"
      />

      <!-- Empty state -->
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <div
        v-if="conversations.length === 0"
        class="text-center py-10 text-n-slate-10"
      >
        <p class="text-xs">Nenhuma conversa</p>
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template -->

      <!-- Load more button -->
      <button
        v-if="hasMore && conversations.length > 0"
        class="w-full py-2 px-3 mt-1 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-lg transition-colors flex items-center justify-center gap-2"
        :disabled="isLoadingMore"
        @click="handleLoadMore"
      >
        <span
          v-if="isLoadingMore"
          class="animate-spin h-3 w-3 border-2 border-n-slate-8 border-t-n-slate-11 rounded-full"
        />
        <span v-else>{{ loadMoreText }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.kanban-column {
  min-width: 280px;
  max-width: 280px;
  transition: all 0.15s ease;
}

.column-content {
  max-height: calc(100vh - 200px);
  min-height: 200px;
}

.column-content::-webkit-scrollbar {
  width: 4px;
}

.column-content::-webkit-scrollbar-track {
  background: transparent;
}

.column-content::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 2px;
}

.column-content::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
